import React, { useEffect, useRef, useState } from 'react';
import catalog from './lgControlCatalog.json';
import axios from 'axios';
import { buildLgFieldSessionZip, checklistControlId } from './lgFieldSessionExport';
import { saveLogDownload } from '../SiteControl/deviceLogPackage';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const READ_BATCH = ['system', 'software', 'inputs', 'apps', 'foreground', 'power', 'volume', 'output'];

export default function LgCommissioningPanel({ deviceId, name, siteId, onCommand, onStop, onInputs }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [attempted, setAttempted] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({});
  const [state, setState] = useState({});
  const [number, setNumber] = useState('1');
  const [enter, setEnter] = useState(false);
  const [digitDelay, setDigitDelay] = useState(250);
  const [enterDelay, setEnterDelay] = useState(500);
  const [repeat, setRepeat] = useState(1);
  const [interval, setInterval] = useState(150);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [capture, setCapture] = useState(null);
  const generation = useRef(0);
  const active = useRef(deviceId);
  active.current = deviceId;
  const stop = () => { generation.current++; onStop(); setBusy(false); };
  useEffect(() => {
    generation.current++;
    setDraft({}); setSelected(null); setState({}); setAttempted(new Set()); setMessage(''); setBusy(false);
    setCapture(null); setNumber('1'); setCustom(''); setRepeat(1); setEnter(false);
    const cancel = () => { generation.current++; onStop(); setBusy(false); };
    const hide = () => { if (document.hidden) cancel(); };
    window.addEventListener('offline', cancel);
    window.addEventListener('pagehide', cancel);
    document.addEventListener('visibilitychange', hide);
    return () => {
      generation.current++;
      window.removeEventListener('offline', cancel);
      window.removeEventListener('pagehide', cancel);
      document.removeEventListener('visibilitychange', hide);
    };
  }, [deviceId, onStop]);

  const send = async (id, params = {}) => {
    const target = deviceId;
    setAttempted((previous) => new Set([...previous, id]));
    let evidence;
    const outcome = await onCommand(id, { operationParams: params, onResult: (result) => {
      evidence = result;
      if (active.current !== target) return;
      if (result.attachment) setCapture(result.attachment);
      if (id === 'lg_op_inputs' && result.observation?.value?.devices) onInputs?.(result.observation.value.devices);
      if (result.observation) setState((previous) => ({ ...previous, [id]: result.observation }));
      setMessage(`${id}: ${result.attempt?.diagnostics?.deliveryStage || 'delivery unknown'}; visible effect needs observation.`);
    } });
    return { ...outcome, evidence };
  };
  const runBounded = async (id, params) => {
    const epoch = ++generation.current;
    setBusy(true);
    try { return await send(id, params); }
    finally { if (generation.current === epoch) setBusy(false); }
  };
  const runSequence = async (actions, independent = false) => {
    const epoch = ++generation.current;
    setBusy(true); setMessage('Running bounded test; Stop discards unsent work.');
    try {
      for (const [id, delay] of actions) {
        if (generation.current !== epoch) break;
        if (delay) await sleep(delay);
        if (generation.current !== epoch) break;
        const result = await send(id);
        if (result?.status !== 'sent' && !independent) break;
      }
    } finally { if (generation.current === epoch) setBusy(false); }
  };
  const run = async (item, params = {}) => {
    if (item.unavailable) return;
    if (item.repeat && repeat > 1) {
      return runBounded('lg_op_repeat', { button: item.token, count: repeat, intervalMs: interval });
    }
    const epoch = generation.current;
    const result = await send(item.id, params);
    if (generation.current !== epoch || result?.status !== 'sent') return;
    if (['lg_op_input', 'lg_op_app_launch'].includes(item.id)) {
      // These are separate fresh observations, never an invented launch/input acknowledgement.
      for (let index = 0; index < (item.id === 'lg_op_app_launch' ? 3 : 1); index++) {
        if (generation.current !== epoch) return;
        const fresh = await send('lg_op_foreground');
        const appId = fresh.evidence?.observation?.value?.appId;
        if (appId === params.id) {
          setMessage(`Observed foreground app ${appId}; in-app playback remains unverified.`);
          break;
        }
        if (index < 2 && item.id === 'lg_op_app_launch') await sleep(500);
      }
      if (item.id === 'lg_op_input' && generation.current === epoch) await send('lg_op_inputs');
    }
  };
  const submit = async () => {
    try {
      const params = Object.fromEntries((selected.fields || []).flatMap(([key, , type]) => {
        const value = draft[key];
        if (type === 'json' && !value) return [];
        if (key === 'contentId' && !value) return [];
        return [[key, type === 'number' ? Number(value) : type === 'boolean' ? Boolean(value)
          : type === 'json' ? JSON.parse(value) : value || '']];
      }));
      await run(selected, params);
    } catch { setMessage('Invalid fields or JSON. Nothing further sent.'); }
  };
  const restoreHistory = async () => {
    const target = deviceId;
    try {
      const saved = JSON.parse(localStorage.getItem(`lgFieldSession:${siteId}`));
      if (!saved?.id) { setMessage('No retained field session yet.'); return; }
      const response = await axios.get(`/api/site-control/targets/${deviceId}/commissioning-sessions/${saved.id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (active.current !== target) return;
      const entries = (response.data.attempts || []).filter((entry) => Number(entry.attempt?.target?.deviceId) === Number(target));
      setAttempted(new Set(entries.map((entry) => checklistControlId(entry.attempt))));
      const restored = {};
      entries.forEach((entry) => { if (entry.observation) restored[entry.attempt.action] = { ...entry.observation, stale: true, freshness: 'retained history; request a fresh read' }; });
      setState(restored);
      setMessage(response.data.session?.truncated ? 'Retained history is truncated at the session limit.' : 'Retained history loaded. State is historical until refreshed.');
    } catch { if (active.current === target) setMessage('Retained history unavailable or expired; check the session migration.'); }
  };
  const exportSession = async () => {
    try {
      const saved = JSON.parse(localStorage.getItem(`lgFieldSession:${siteId}`));
      if (!saved?.id) { setMessage('No field session yet. Send a control first.'); return; }
      const response = await axios.get(`/api/site-control/targets/${deviceId}/commissioning-sessions/${saved.id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const attachments = JSON.parse(sessionStorage.getItem(`lgFieldAttachments:${saved.id}`) || '[]');
      const zip = await buildLgFieldSessionZip(response.data, attachments);
      saveLogDownload(zip, `suitestream-lg-field-${saved.id}.zip`);
      setMessage('Field session exported without sending device commands.');
    } catch (error) { setMessage(error.response?.data?.message || 'Session export unavailable. Check server migration and session expiry.'); }
  };
  const inventory = state.lg_op_inputs?.value?.devices || [];
  const apps = state.lg_op_apps?.value?.launchPoints || state.lg_op_apps?.value?.apps || [];
  const visible = catalog.filter((item) => `${item.label} ${item.id} ${item.group}`.toLowerCase().includes(search.toLowerCase())
    && (filter === 'all' || (filter === 'untested' ? !attempted.has(item.id) : attempted.has(item.id))));
  const groups = Array.from(new Set(visible.map((item) => item.group)));
  return <section className="dashboard-tv-remote-panel">
    <h4>Advanced · {name} · target {deviceId}</h4>
    <p>All executable controls are unverified candidates. Native tuner commands do not establish FPT channel control.
      Service keys only open a service menu; never change service settings. The shared Fire Stick uses its separate ADB remote.</p>
    <button type="button" onClick={stop}>Stop</button>
    <button type="button" onClick={restoreHistory}>Load retained history</button>
    <button type="button" onClick={exportSession}>Export Field Session</button>
    <button type="button" disabled={busy} onClick={() => runSequence(READ_BATCH.map((id) => [`lg_op_${id}`, 0]), true)}>Refresh diagnostics (independent reads)</button>
    <fieldset disabled={busy}>
      <legend>Channel-number test · first test 1 / VTV1 in FPT</legend>
      <label>Channel (1–3 digits)<input aria-label="LG channel number" value={number} maxLength={3} onChange={(e) => setNumber(e.target.value)} /></label>
      <label>Digit delay (100–2000 ms)<input type="number" min="100" max="2000" value={digitDelay} onChange={(e) => setDigitDelay(Number(e.target.value))} /></label>
      <label><input type="checkbox" checked={enter} onChange={(e) => setEnter(e.target.checked)} />Final pointer Enter</label>
      <label>Pre-Enter delay (100–2000 ms)<input type="number" min="100" max="2000" value={enterDelay} onChange={(e) => setEnterDelay(Number(e.target.value))} /></label>
      <button type="button" disabled={!/^\d{1,3}$/.test(number) || digitDelay < 100 || digitDelay > 2000 || enterDelay < 100 || enterDelay > 2000}
        onClick={() => runBounded('lg_op_channel', { digits: number, enter, digitDelayMs: digitDelay, preEnterDelayMs: enterDelay })}>Send channel test</button>
    </fieldset>
    <p>Inputs: {inventory.length ? 'reported native IDs' : 'inventory unavailable; HDMI 1–4 below are candidates, not confirmed sockets'}</p>
    <div>{(inventory.length ? inventory : [1, 2, 3, 4].map((port) => ({ id: `HDMI_${port}`, label: `HDMI ${port} candidate` }))).map((input) =>
      <button type="button" key={input.id} disabled={busy} onClick={() => run({ id: 'lg_op_input' }, { id: input.id })}>{input.label || input.id} · {input.id}</button>)}</div>
    <label>Repeat taps (D-pad / volume / channel only)<input type="number" min="1" max="20" value={repeat} onChange={(e) => setRepeat(Math.min(20, Math.max(1, Number(e.target.value) || 1)))} /></label>
    <label>Tap interval (100–1000 ms)<input type="number" min="100" max="1000" value={interval} onChange={(e) => setInterval(Math.min(1000, Math.max(100, Number(e.target.value) || 150)))} /></label>
    <label>Search All Controls<input value={search} onChange={(e) => setSearch(e.target.value)} /></label>
    <label>Checklist filter<select value={filter} onChange={(e) => setFilter(e.target.value)}>
      <option value="all">All controls</option><option value="untested">Not attempted</option><option value="attempted">Attempted (inspect observations)</option>
    </select></label>
    <button type="button" onClick={() => { const next = catalog.find((item) => !item.unavailable && !item.helper && !attempted.has(item.id)); if (next) { setFilter('all'); setSearch(next.id); } }}>Next untested (view only)</button>
    {groups.map((group) => <details key={group} open={search ? true : undefined}><summary>{group}</summary>
      {visible.filter((item) => item.group === group).map((item) => <div key={item.id}>
        <button type="button" disabled={busy || Boolean(item.unavailable) || Boolean(item.helper)} onClick={() => {
          if (item.fields?.length) { setSelected(item); setDraft({}); }
          else run(item);
        }}>{item.label}</button>
        <small>{attempted.has(item.id) ? 'Attempted; see observation · ' : 'Not tested · '}{item.unavailable || `${item.token || item.id} · ${item.variant} · executable, hardware unverified${item.repeat ? ' · repeat taps allowed' : ' · one-shot only'}`}</small>
      </div>)}
    </details>)}
    {selected && <fieldset disabled={busy}><legend>{selected.label} · {name}</legend>
      {(selected.fields || []).map(([key, label, type]) => <label key={key}>{label}
        {['input', 'app'].includes(type) ? <select value={draft[key] || ''} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}>
          <option value="">Read inventory and choose an actual ID</option>
          {(type === 'app' ? apps : inventory).map((item) => <option key={item.id} value={item.id}>{item.title || item.label || item.id} · {item.id}</option>)}
        </select> : type === 'boolean' ? <input type="checkbox" checked={Boolean(draft[key])} onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })} />
          : <input type={type === 'number' ? 'number' : 'text'} value={draft[key] || ''} maxLength={key === 'text' ? 512 : 4096} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />}
      </label>)}
      <button type="button" onClick={submit}>Send {selected.label}</button>
    </fieldset>}
    <label>One-shot custom pointer token<input value={custom} maxLength={64} onChange={(e) => setCustom(e.target.value)} /></label>
    <button type="button" disabled={busy || !/^[A-Z0-9_]{1,64}$/.test(custom) || custom === 'IN_STOP'} onClick={() => send(`lg_custom_${custom}`)}>Send custom candidate once</button>
    <p role="status">{message}</p>
    <details><summary>State / diagnostic replies (timestamps and sources)</summary><pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(state, null, 2)}</pre></details>
    {capture && <figure><img alt="Explicit one-shot TV capture; protected video may be absent" src={`data:${capture.mimeType};base64,${capture.base64}`} /></figure>}
    <p>One-shot image retrieval requires the TV to return an HTTP(S) image on its own host. Relay logcat is a separate capture; missing evidence is listed in exports.</p>
  </section>;
}
